/**
 * IPC CRR CSV Utilities
 * Handles CSV operations for IPC CRR data
 */

export interface IpcCrrCsvRow {
  do_from: string;
  do_to: string;
  year: string;
  product: string;
  rg: string;
  service: string;
  td_rate_kg: string;
  td_rate_item: string;
}

export interface IpcCrrRateData {
  direction: 'inbound' | 'outbound';
  groupName: string;
  groupDisplayName: string;
  format: string;
  type: 'tdKg' | 'tdItem';
  rate: number;
  service: 'Priority' | 'Non-Priority';
}

/**
 * CN61 extra data extracted from CRR CSV upload
 * These are fields that don't belong to CRR but can be used in CN61
 */
export interface IpcCn61ExtraData {
  direction: 'inbound' | 'outbound';
  format: string; // Postcards, Registered, Insured, Express, M-Bags
  type: 'weight' | 'items';
  rate: number;
  service: 'Priority' | 'Non-Priority';
}

/**
 * Result of parsing CRR CSV with extra CN61 data
 */
export interface IpcCrrCsvParseResult {
  crrData: IpcCrrRateData[];
  cn61ExtraData: IpcCn61ExtraData[];
}

/**
 * Parse CSV string for IPC CRR data with CN61 extra data extraction
 * @param csvString - CSV content as string
 * @param currentData - Current CRR data structure
 * @param currentUserPpo - Current user's designated operator
 * @param expectedYear - Expected year to validate against
 * @returns Parsed CRR rate data and CN61 extra data
 */
export function parseIpcCrrCsvWithCn61Extra(csvString: string, currentData?: any, currentUserPpo?: string, expectedYear?: number): IpcCrrCsvParseResult {
  const lines = csvString.trim().split('\n');
  
  if (lines.length < 2) {
    throw new Error('CSV file must contain at least a header row and one data row');
  }

  // Parse header - support both old format (ppo_from) and new format (do_from)
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  
  // Map old header names to new ones
  const headerMap: { [key: string]: string } = {
    'ppo_from': 'do_from',
    'ppo_to': 'do_to'
  };
  
  const normalizedHeaders = headers.map(h => headerMap[h] || h);
  
  const expectedHeaders = ['do_from', 'do_to', 'year', 'service', 'product', 'rg', 'td_rate_kg', 'td_rate_item'];
  
  if (!expectedHeaders.every(header => normalizedHeaders.includes(header))) {
    throw new Error('CSV file must contain the required headers: do_from (or ppo_from), do_to (or ppo_to), year, service, product, rg, td_rate_kg, td_rate_item');
  }

  const crrData: IpcCrrRateData[] = [];
  const cn61ExtraData: IpcCn61ExtraData[] = [];
  const errors: string[] = [];

  // CRR valid products (Format P, G, E)
  const crrValidProducts = ['P', 'G', 'E', 'FORMAT P', 'FORMAT G', 'FORMAT E'];
  
  // CN61 extra products (not valid for CRR but valid for CN61)
  const cn61ExtraProducts = ['POSTCARDS', 'REGISTERED', 'INSURED', 'EXPRESS', 'M-BAGS'];

  function sanitizeNumericCell(cell: string): string {
    if (cell == null) return '';
    const trimmed = cell.trim();
    // Remove leading apostrophe used to force Excel text formatting
    if (trimmed.startsWith("'")) return trimmed.slice(1);
    // Handle Excel formula-style text wrapper: ="2.000"
    if (/^=\".*\"$/.test(trimmed)) {
      return trimmed.substring(2, trimmed.length - 1);
    }
    return trimmed;
  }

  function validateThreeDecimals(cell: string, fieldName: 'td_rate_kg' | 'td_rate_item', rowIdx: number): boolean {
    if (cell === '') return true; // allow empty as zero
    const threeDecimalPattern = /^\d+\.\d{3}$/;
    if (!threeDecimalPattern.test(cell)) {
      errors.push(`Row ${rowIdx}: ${fieldName} must have exactly 3 decimal places (e.g., 2.000)`);
      return false;
    }
    return true;
  }

  // Get header indices using normalized headers
  const getHeaderIndex = (headerName: string) => {
    return normalizedHeaders.indexOf(headerName);
  };
  
  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const values = line.split(',').map(v => v.trim());
    
    if (values.length !== normalizedHeaders.length) {
      errors.push(`Row ${i + 1}: Invalid number of columns. Expected ${normalizedHeaders.length}, got ${values.length}`);
      continue; // Skip this row but continue validation
    }

    const row: IpcCrrCsvRow = {
      do_from: values[getHeaderIndex('do_from')],
      do_to: values[getHeaderIndex('do_to')],
      year: values[getHeaderIndex('year')],
      service: values[getHeaderIndex('service')],
      product: values[getHeaderIndex('product')],
      rg: values[getHeaderIndex('rg')],
      td_rate_kg: values[getHeaderIndex('td_rate_kg')],
      td_rate_item: values[getHeaderIndex('td_rate_item')]
    };

    let hasRowError = false;

    // Validate required fields - rg is optional for CN61 extra products
    const productUpper = row.product.toUpperCase();
    const isCn61ExtraProduct = cn61ExtraProducts.includes(productUpper);
    
    if (!row.do_from || !row.do_to || !row.year || !row.product || !row.service) {
      errors.push(`Row ${i + 1}: Missing required fields`);
      hasRowError = true;
    }
    
    // rg is required for CRR products but optional for CN61 extra products
    if (!isCn61ExtraProduct && !row.rg) {
      errors.push(`Row ${i + 1}: Missing required field 'rg' for CRR product`);
      hasRowError = true;
    }

    // Validate year if expectedYear is provided
    if (expectedYear !== undefined) {
      const rowYear = parseInt(row.year, 10);
      if (isNaN(rowYear)) {
        errors.push(`Row ${i + 1}: Invalid year value '${row.year}'. Must be a valid number`);
        hasRowError = true;
      } else if (rowYear !== expectedYear) {
        errors.push(`Row ${i + 1}: Year mismatch. Expected ${expectedYear} but got ${rowYear}`);
        hasRowError = true;
      }
    }

    // Determine if this is a CRR product or CN61 extra product
    const normalizedProduct = productUpper.replace('FORMAT ', '');
    const isCrrProduct = crrValidProducts.includes(productUpper) || ['P', 'G', 'E'].includes(normalizedProduct);
    
    if (!isCrrProduct && !isCn61ExtraProduct) {
      errors.push(`Row ${i + 1}: Invalid product '${row.product}'. Must be 'Format P', 'Format G', 'Format E', 'Postcards', 'Registered', 'Insured', 'Express', or 'M-Bags'`);
      hasRowError = true;
    }

    // Validate service
    if (!['Priority', 'Non-Priority'].includes(row.service)) {
      errors.push(`Row ${i + 1}: Invalid service. Must be 'Priority' or 'Non-Priority'`);
      hasRowError = true;
    }

    // Sanitize and validate exact 3-decimal inputs
    const tdRateKgStr = sanitizeNumericCell(row.td_rate_kg);
    const tdRateItemStr = sanitizeNumericCell(row.td_rate_item);
    const kgValid = validateThreeDecimals(tdRateKgStr, 'td_rate_kg', i + 1);
    const itemValid = validateThreeDecimals(tdRateItemStr, 'td_rate_item', i + 1);
    
    if (!kgValid || !itemValid) {
      hasRowError = true;
    }

    // Parse rates - treat empty strings as 0
    const tdRateKg = tdRateKgStr === '' ? 0 : parseFloat(tdRateKgStr);
    const tdRateItem = tdRateItemStr === '' ? 0 : parseFloat(tdRateItemStr);

    if (isNaN(tdRateKg) || tdRateKg < 0) {
      errors.push(`Row ${i + 1}: Invalid td_rate_kg value. Must be a non-negative number`);
      hasRowError = true;
    }

    if (isNaN(tdRateItem) || tdRateItem < 0) {
      errors.push(`Row ${i + 1}: Invalid td_rate_item value. Must be a non-negative number`);
      hasRowError = true;
    }

    // Skip adding data if this row has errors
    if (hasRowError) {
      continue;
    }

    // Determine direction based on do_from and do_to
    const filterPpo = currentData?.dto?.ppo || 'UNKNOWN';
    const userPpo = currentUserPpo || filterPpo;
    const direction = row.do_from === filterPpo ? 'inbound' : 'outbound';

    const service = row.service as 'Priority' | 'Non-Priority';

    if (isCrrProduct) {
      // Add to CRR data
      const normalizedProductFormat = row.product.toUpperCase().replace('FORMAT ', '');
      
      crrData.push({
        direction,
        groupName: row.rg,
        groupDisplayName: row.rg,
        format: normalizedProductFormat,
        type: 'tdKg',
        rate: tdRateKg,
        service
      });

      crrData.push({
        direction,
        groupName: row.rg,
        groupDisplayName: row.rg,
        format: normalizedProductFormat,
        type: 'tdItem',
        rate: tdRateItem,
        service
      });
    } else if (isCn61ExtraProduct) {
      // Add to CN61 extra data
      // Map product names to CN61 format names
      let cn61Format = productUpper;
      if (productUpper === 'M-BAGS') {
        cn61Format = 'M-Bags';
      } else {
        // Capitalize first letter only
        cn61Format = productUpper.charAt(0) + productUpper.slice(1).toLowerCase();
      }
      
      // Determine which fields to create based on product type
      const isItemsOnlyProduct = ['REGISTERED', 'INSURED', 'EXPRESS'].includes(productUpper);
      const isWeightOnlyProduct = productUpper === 'M-BAGS';
      
      if (isItemsOnlyProduct) {
        // Only items
        cn61ExtraData.push({
          direction,
          format: cn61Format,
          type: 'items',
          rate: tdRateItem,
          service
        });
      } else if (isWeightOnlyProduct) {
        // Only weight
        cn61ExtraData.push({
          direction,
          format: cn61Format,
          type: 'weight',
          rate: tdRateKg,
          service
        });
      } else {
        // Both weight and items (Postcards)
        cn61ExtraData.push({
          direction,
          format: cn61Format,
          type: 'weight',
          rate: tdRateKg,
          service
        });
        cn61ExtraData.push({
          direction,
          format: cn61Format,
          type: 'items',
          rate: tdRateItem,
          service
        });
      }
    }
  }

  // If there were any errors during validation, throw them all
  if (errors.length > 0) {
    throw new Error(errors.join('|||')); // Use ||| as delimiter to split later
  }

  return { crrData, cn61ExtraData };
}

/**
 * Parse CSV string for IPC CRR data (backward compatible version)
 * @param csvString - CSV content as string
 * @param currentData - Current CRR data structure
 * @param currentUserPpo - Current user's designated operator
 * @param expectedYear - Expected year to validate against
 * @returns Parsed CRR rate data
 */
export function parseIpcCrrCsv(csvString: string, currentData?: any, currentUserPpo?: string, expectedYear?: number): IpcCrrRateData[] {
  const result = parseIpcCrrCsvWithCn61Extra(csvString, currentData, currentUserPpo, expectedYear);
  return result.crrData;
}

/**
 * Convert IPC CRR data to CSV format
 * @param data - CRR data structure
 * @param currentUserPpo - Current user's designated operator
 * @returns CSV string
 */
export function convertIpcCrrDataToCsv(data: any, currentUserPpo?: string): string {
  const csvRows: string[] = [];
  
  // Add header
  csvRows.push('do_from,do_to,year,service,product,rg,td_rate_kg,td_rate_item');
  
  const filterPpo = data?.dto?.ppo || 'UNKNOWN';
  const userPpo = currentUserPpo || filterPpo;
  const year = data?.dto?.year || new Date().getFullYear();

  // Ensure values are consistently formatted with 3 decimals for download
  function formatTo3Decimals(value: any): string {
    if (value === null || value === undefined || value === '') return '0.000';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0.000';
    return Number(num).toFixed(3);
  }

  // Use Excel formula-style text to preserve trailing zeros in Excel
  function formatCsvValueForExport(value: any): string {
    return formatTo3Decimals(value);
  }

  // Process inbound data
  if (data.command?.inbound) {
    const inbound = data.command.inbound;
    
    // Process priority data
    if (inbound.priority) {
      inbound.priority.forEach((item: any) => {
        const groupName = item.remunerationGroup?.displayName || item.remunerationGroup?.name || 'UPU';
        ['formatP', 'formatG', 'formatE'].forEach(formatKey => {
          const format = item[formatKey];
          if (format) {
            const product = `Format ${formatKey.replace('format', '')}`;
            const tdRateKg = format.tdKg || 0;
            const tdRateItem = format.tdItem || 0;
            // For inbound: do_from = filter DO (other PPO), do_to = current user DO (receiving from other)
            const weightValue = formatCsvValueForExport(tdRateKg);
            const itemValue = formatCsvValueForExport(tdRateItem);
            csvRows.push(`${filterPpo},${userPpo},${year},Priority,${product},${groupName},${weightValue},${itemValue}`);
          }
        });
      });
    }
    
    // Process non-priority data
    if (inbound.nonPriority) {
      inbound.nonPriority.forEach((item: any) => {
        const groupName = item.remunerationGroup?.displayName || item.remunerationGroup?.name || 'UPU';
        ['formatP', 'formatG', 'formatE'].forEach(formatKey => {
          const format = item[formatKey];
          if (format) {
            const product = `Format ${formatKey.replace('format', '')}`;
            const tdRateKg = format.tdKg || 0;
            const tdRateItem = format.tdItem || 0;
            // For inbound: do_from = filter DO (other PPO), do_to = current user DO (receiving from other)
            const weightValue = formatCsvValueForExport(tdRateKg);
            const itemValue = formatCsvValueForExport(tdRateItem);
            csvRows.push(`${filterPpo},${userPpo},${year},Non-Priority,${product},${groupName},${weightValue},${itemValue}`);
          }
        });
      });
    }
  }

  // Process outbound data
  if (data.command?.outbound) {
    const outbound = data.command.outbound;
    
    // Process priority data
    if (outbound.priority) {
      outbound.priority.forEach((item: any) => {
        const groupName = item.remunerationGroup?.displayName || item.remunerationGroup?.name || 'UPU';
        ['formatP', 'formatG', 'formatE'].forEach(formatKey => {
          const format = item[formatKey];
          if (format) {
            const product = `Format ${formatKey.replace('format', '')}`;
            const tdRateKg = format.tdKg || 0;
            const tdRateItem = format.tdItem || 0;
            // For outbound: do_from = current user DO, do_to = filter DO (other PPO) (sending to other)
            const weightValue = formatCsvValueForExport(tdRateKg);
            const itemValue = formatCsvValueForExport(tdRateItem);
            csvRows.push(`${userPpo},${filterPpo},${year},Priority,${product},${groupName},${weightValue},${itemValue}`);
          }
        });
      });
    }
    
    // Process non-priority data
    if (outbound.nonPriority) {
      outbound.nonPriority.forEach((item: any) => {
        const groupName = item.remunerationGroup?.displayName || item.remunerationGroup?.name || 'UPU';
        ['formatP', 'formatG', 'formatE'].forEach(formatKey => {
          const format = item[formatKey];
          if (format) {
            const product = `Format ${formatKey.replace('format', '')}`;
            const tdRateKg = format.tdKg || 0;
            const tdRateItem = format.tdItem || 0;
            // For outbound: do_from = current user DO, do_to = filter DO (other PPO) (sending to other)
            const weightValue = formatCsvValueForExport(tdRateKg);
            const itemValue = formatCsvValueForExport(tdRateItem);
            csvRows.push(`${userPpo},${filterPpo},${year},Non-Priority,${product},${groupName},${weightValue},${itemValue}`);
          }
        });
      });
    }
  }

  return csvRows.join('\n');
}

/**
 * Generate sample CSV for IPC CRR
 * @param currentUserPpo - Current user's designated operator
 * @param filterPpo - Filter PPO
 * @param year - Year
 * @returns Sample CSV string
 */
export function generateIpcCrrSampleCsv(currentUserPpo: string = 'CHA', filterPpo: string = 'USA', year: number = 2021): string {
  const sampleData = [
    `${currentUserPpo},${filterPpo},${year},Priority,Format P,UPU,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Priority,Format G,UPU,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Priority,Format E,UPU,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Format P,UPU,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Format G,UPU,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Format E,UPU,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,Format P,UPU,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,Format G,UPU,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,Format E,UPU,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Format P,UPU,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Format G,UPU,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Format E,UPU,2.000,2.000`
  ];

  return `do_from,do_to,year,service,product,rg,td_rate_kg,td_rate_item\n${sampleData.join('\n')}`;
}

/**
 * Apply CSV data to IPC CRR data structure
 * @param csvData - Parsed CSV data
 * @param baseData - Base CRR data structure
 * @returns Updated CRR data
 */
export function applyCsvDataToIpcCrr(csvData: IpcCrrRateData[], baseData: any): any {
  const updatedData = { ...baseData };
  
  // Helper function to normalize group names for matching (trim and lowercase)
  const normalizeKey = (str: string): string => {
    return (str || '').trim().toLowerCase();
  };
  
  // Build a map of existing remuneration groups from baseData to preserve their IDs
  // Use multiple keys to match groups by name, displayName, or combinations
  const existingGroupsMap = new Map<string, any>();
  
  // Helper to add group to map with multiple key variations
  const addGroupToMap = (group: any) => {
    if (!group) return;
    const name = normalizeKey(group.name || '');
    const displayName = normalizeKey(group.displayName || '');
    
    // Add with various key combinations for flexible matching
    existingGroupsMap.set(`${name}_${displayName}`, group);
    existingGroupsMap.set(`${displayName}_${name}`, group);
    existingGroupsMap.set(name, group);
    existingGroupsMap.set(displayName, group);
    // Also add original case-sensitive keys for exact matches
    if (group.name && group.displayName) {
      existingGroupsMap.set(`${group.name}_${group.displayName}`, group);
      existingGroupsMap.set(`${group.displayName}_${group.name}`, group);
    }
  };
  
  // Collect from inbound priority data
  if (baseData?.command?.inbound?.priority) {
    baseData.command.inbound.priority.forEach((item: any) => {
      addGroupToMap(item.remunerationGroup);
    });
  }
  
  // Collect from inbound non-priority data
  if (baseData?.command?.inbound?.nonPriority) {
    baseData.command.inbound.nonPriority.forEach((item: any) => {
      addGroupToMap(item.remunerationGroup);
    });
  }
  
  // Collect from outbound priority data
  if (baseData?.command?.outbound?.priority) {
    baseData.command.outbound.priority.forEach((item: any) => {
      addGroupToMap(item.remunerationGroup);
    });
  }
  
  // Collect from outbound non-priority data
  if (baseData?.command?.outbound?.nonPriority) {
    baseData.command.outbound.nonPriority.forEach((item: any) => {
      addGroupToMap(item.remunerationGroup);
    });
  }
  
  // Helper function to find existing group by CSV group name
  const findExistingGroup = (csvGroupName: string, csvGroupDisplayName: string): any => {
    const normalizedName = normalizeKey(csvGroupName);
    const normalizedDisplayName = normalizeKey(csvGroupDisplayName);
    
    // Try multiple matching strategies
    return existingGroupsMap.get(`${normalizedName}_${normalizedDisplayName}`) ||
           existingGroupsMap.get(`${normalizedDisplayName}_${normalizedName}`) ||
           existingGroupsMap.get(normalizedName) ||
           existingGroupsMap.get(normalizedDisplayName) ||
           existingGroupsMap.get(`${csvGroupName}_${csvGroupDisplayName}`) ||
           existingGroupsMap.get(`${csvGroupDisplayName}_${csvGroupName}`) ||
           null;
  };
  
  // Initialize structures if they don't exist
  if (!updatedData.command) {
    updatedData.command = {
      inbound: null,
      outbound: null
    };
  }
  
  // Only initialize structures for directions that have data
  const hasInboundData = csvData.some(row => row.direction === 'inbound');
  const hasOutboundData = csvData.some(row => row.direction === 'outbound');
  
  if (hasInboundData && !updatedData.command.inbound) {
    updatedData.command.inbound = {
      priority: [],
      nonPriority: []
    };
  }
  if (hasOutboundData && !updatedData.command.outbound) {
    updatedData.command.outbound = {
      priority: [],
      nonPriority: []
    };
  }
  
  // Group CSV data by direction, service, and group
  const groupedData: any = {
    inbound: {
      priority: {},
      'non-priority': {}
    },
    outbound: {
      priority: {},
      'non-priority': {}
    }
  };
  
  csvData.forEach(row => {
    const key = `${row.groupName}_${row.groupDisplayName}`;
    // Convert service key to match the groupedData structure
    const serviceKey = row.service === 'Priority' ? 'priority' : 'non-priority';
    
    if (!groupedData[row.direction][serviceKey][key]) {
      groupedData[row.direction][serviceKey][key] = {
        groupName: row.groupName,
        groupDisplayName: row.groupDisplayName,
        formats: {}
      };
    }
    
    if (!groupedData[row.direction][serviceKey][key].formats[row.format]) {
      groupedData[row.direction][serviceKey][key].formats[row.format] = {};
    }
    
    groupedData[row.direction][serviceKey][key].formats[row.format][row.type] = Number(row.rate).toFixed(3);
  });
  
  // Convert grouped data to CRR format - only for directions that have data
  Object.keys(groupedData).forEach(direction => {
    const directionKey = direction === 'inbound' ? 'inbound' : 'outbound';
    
    // Only update the direction that has CSV data, preserve the other direction
    if (direction === 'inbound' && hasInboundData) {
      updatedData.command[directionKey] = {
        priority: [],
        nonPriority: []
      };
    } else if (direction === 'outbound' && hasOutboundData) {
      updatedData.command[directionKey] = {
        priority: [],
        nonPriority: []
      };
    }
    
    // Only process if this direction has data
    if ((direction === 'inbound' && hasInboundData) || (direction === 'outbound' && hasOutboundData)) {
      // Process both priority and non-priority service types
      ['priority', 'non-priority'].forEach(serviceType => {
        const targetArray = serviceType === 'priority' ? 'priority' : 'nonPriority';
        
        Object.values(groupedData[direction][serviceType]).forEach((group: any) => {
          // Look up existing group to preserve ID and other properties
          const existingGroup = findExistingGroup(group.groupName, group.groupDisplayName);
          
          const crrItem = {
            remunerationGroup: existingGroup ? {
              id: existingGroup.id,
              name: existingGroup.name,
              displayName: existingGroup.displayName,
              operator: existingGroup.operator || null,
              predefined: existingGroup.predefined || false,
              homeCountry: existingGroup.homeCountry || false
            } : {
              id: 0,
              name: group.groupName,
              displayName: group.groupDisplayName,
              operator: null,
              predefined: false,
              homeCountry: false
            },
            formatP: null,
            formatG: null,
            formatE: null
          };
          
          // Apply format data
          Object.keys(group.formats).forEach(format => {
            const formatKey = `format${format}` as keyof typeof crrItem;
            const formatData = group.formats[format];
            
            if (formatData.tdKg !== undefined || formatData.tdItem !== undefined) {
              (crrItem as any)[formatKey] = {
                tdKg: formatData.tdKg !== undefined ? formatData.tdKg : null,
                tdItem: formatData.tdItem !== undefined ? formatData.tdItem : null
              };
            }
          });
          
          // Add to the correct array based on service type
          updatedData.command[directionKey][targetArray].push(crrItem);
        });
      });
    }
  });
  
  return updatedData;
}

/**
 * Download CSV file
 * @param csvContent - CSV string content
 * @param filename - Name of the file to download
 */
export function downloadIpcCrrCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Convert CN61 extra data to CN61 input format
 * This is used when passing extra data from CRR page to CN61 page
 * @param cn61ExtraData - CN61 extra data extracted from CRR CSV
 * @returns CN61 input format object
 */
export function convertCn61ExtraDataToInputFormat(cn61ExtraData: IpcCn61ExtraData[]): any {
  const result: any = {
    inboundCn61Input: {},
    outboundCn61Input: {}
  };

  // Format rate to exactly 3 decimals
  const formatRate = (rate: number): string => {
    return rate.toFixed(3);
  };

  // Map format names to CN61 input keys
  const formatToKeyMap: { [key: string]: string } = {
    'Postcards': 'postcards',
    'Registered': 'registered',
    'Insured': 'insured',
    'Express': 'express',
    'M-Bags': 'mbags'
  };

  cn61ExtraData.forEach(row => {
    const inputKey = row.direction === 'inbound' ? 'inboundCn61Input' : 'outboundCn61Input';
    const formatKey = formatToKeyMap[row.format];
    
    if (!formatKey) return;

    if (!result[inputKey][formatKey]) {
      result[inputKey][formatKey] = {};
    }

    // Determine the structure based on product type
    const isItemsOnlyProduct = ['registered', 'insured', 'express'].includes(formatKey);
    const isWeightOnlyProduct = formatKey === 'mbags';

    if (isItemsOnlyProduct && row.type === 'items') {
      if (!result[inputKey][formatKey].items) {
        result[inputKey][formatKey].items = {};
      }
      if (row.service === 'Priority') {
        result[inputKey][formatKey].items.priority = formatRate(row.rate);
      } else {
        result[inputKey][formatKey].items.nonPriority = formatRate(row.rate);
      }
    } else if (isWeightOnlyProduct && row.type === 'weight') {
      if (!result[inputKey][formatKey].weight) {
        result[inputKey][formatKey].weight = {};
      }
      if (row.service === 'Priority') {
        result[inputKey][formatKey].weight.priority = formatRate(row.rate);
      } else {
        result[inputKey][formatKey].weight.nonPriority = formatRate(row.rate);
      }
    } else if (!isItemsOnlyProduct && !isWeightOnlyProduct) {
      // Postcards - both weight and items
      if (!result[inputKey][formatKey][row.type]) {
        result[inputKey][formatKey][row.type] = {};
      }
      if (row.service === 'Priority') {
        result[inputKey][formatKey][row.type].priority = formatRate(row.rate);
      } else {
        result[inputKey][formatKey][row.type].nonPriority = formatRate(row.rate);
      }
    }
  });

  return result;
}
